/**
 * Mock Payment API Service
 * 
 * Service for interacting with the mock Apple Tree Payment API for testing and development
 */

import type {
  StoredPayment,
  StoredPaymentsResponse,
  InteractiveModeResponse,
} from '../types/mockPaymentApi';

const MOCK_API_BASE = '/test-api/apple-tree';

/**
 * Get all stored payment requests
 */
export async function getStoredPayments(): Promise<StoredPayment[]> {
  try {
    const response = await fetch(`${MOCK_API_BASE}/stored-payments`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: StoredPaymentsResponse = await response.json();
    return data.payments;
  } catch (error) {
    console.error('Failed to fetch stored payments:', error);
    throw error;
  }
}

/**
 * Get interactive mode status
 */
export async function getInteractiveMode(): Promise<boolean> {
  try {
    const response = await fetch(`${MOCK_API_BASE}/interactive`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: InteractiveModeResponse = await response.json();
    return data.interactive;
  } catch (error) {
    console.error('Failed to fetch interactive mode:', error);
    throw error;
  }
}

/**
 * Set interactive mode
 */
export async function setInteractiveMode(interactive: boolean): Promise<void> {
  try {
    const response = await fetch(`${MOCK_API_BASE}/interactive`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ interactive }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Failed to set interactive mode:', error);
    throw error;
  }
}

/**
 * Trigger payment webhook manually
 */
export async function triggerPayment(paymentId: string): Promise<void> {
  try {
    const response = await fetch(`${MOCK_API_BASE}/pay/${paymentId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error(`Failed to trigger payment ${paymentId}:`, error);
    throw error;
  }
}

/**
 * Clear all stored payments
 */
export async function clearAllPayments(): Promise<void> {
  try {
    const response = await fetch(`${MOCK_API_BASE}/clear-payments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Failed to clear payments:', error);
    throw error;
  }
}

export const mockPaymentApiService = {
  getStoredPayments,
  getInteractiveMode,
  setInteractiveMode,
  triggerPayment,
  clearAllPayments
};

